import axios from "axios";

import { resolveApiBase } from "./access";

export const API = resolveApiBase(process.env.REACT_APP_BACKEND_URL);

const TOKEN_KEY = "jmh_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
const sessionChanged = () => window.dispatchEvent(new Event("jmh-session-change"));
export const setToken = (t) => { localStorage.setItem(TOKEN_KEY, t); sessionChanged(); };
export const clearToken = () => { localStorage.removeItem(TOKEN_KEY); sessionChanged(); };

export const api = axios.create({ baseURL: API, timeout: 20000 });
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use((response) => response, (error) => {
  const sent = error.config?.headers?.Authorization;
  if (error.response?.status === 401 && sent && sent === `Bearer ${getToken()}`) clearToken();
  return Promise.reject(error);
});

// ---------- Public intake ----------
export async function submitInquiry(payload) {
  const { data } = await api.post("/inquiries", payload);
  return data;
}

export async function uploadAssessmentFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/assessment/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { file_id, original_filename, size, content_type }
}

export async function fetchPortfolio() {
  const { data } = await api.get("/portfolio");
  return data;
}

// ---------- Admin / Founder Inbox ----------
export async function adminLogin(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  setToken(data.token);
  return data.user;
}

export async function adminMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function adminListInquiries(source = "steward_stone_native_assessment") {
  const { data } = await api.get("/admin/inquiries", { params: { source } });
  return data;
}

export async function adminGetInquiry(id) {
  const { data } = await api.get(`/admin/inquiries/${id}`);
  return data;
}

export async function adminSetStatus(id, status) {
  const { data } = await api.patch(`/admin/inquiries/${id}`, { status });
  return data;
}

export async function adminDownloadFile(fileId) {
  const res = await api.get(`/admin/files/${fileId}/download`, { responseType: "blob" });
  return res.data; // Blob
}

// ---------- Admin / Founder Secure Document Intake ----------
export async function adminUploadDocument(file, note = "", batchId = "") {
  const fd = new FormData();
  fd.append("file", file);
  if (note) fd.append("note", note);
  if (batchId) fd.append("batch_id", batchId);
  if (options.previousFileId) fd.append("previous_file_id", options.previousFileId);
  const { data } = await api.post("/admin/documents/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function adminListDocuments() {
  const { data } = await api.get("/admin/documents");
  return data;
}

export async function adminDownloadDocument(fileId) {
  const res = await api.get(`/admin/documents/${fileId}/download`, { responseType: "blob" });
  return res.data; // Blob
}

export async function adminDeleteDocument(fileId) {
  const { data } = await api.delete(`/admin/documents/${fileId}`);
  return data;
}

// ---------- Hardened Founder Vault session (HttpOnly cookie + CSRF; NO localStorage token) ----------
export const vaultApi = axios.create({ baseURL: API, withCredentials: true });
let _csrf = null;
export const setVaultCsrf = (t) => { _csrf = t; };
vaultApi.interceptors.request.use((config) => {
  const m = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(m) && _csrf) {
    config.headers["X-CSRF-Token"] = _csrf;
  }
  return config;
});

export async function vaultLogin(email, password) {
  const { data } = await vaultApi.post("/vault/session", { email, password });
  setVaultCsrf(data.csrf_token);
  return data;
}
export async function vaultCheck() {
  const { data } = await vaultApi.get("/vault/session/check");
  setVaultCsrf(data.csrf_token);
  return data;
}
let _vaultResume = null;
export async function vaultResumeFounder() {
  if (!_vaultResume) {
    const token = getToken();
    if (!token) throw new Error("Please sign in to Founder Office.");
    _vaultResume = vaultApi.post("/vault/session/from-founder", {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => { setVaultCsrf(data.csrf_token); return data; })
      .catch((error) => { if (error.response?.status === 401) clearToken(); throw error; })
      .finally(() => { _vaultResume = null; });
  }
  return _vaultResume;
}
vaultApi.interceptors.response.use((response) => response, async (error) => {
  const config = error.config;
  if (error.response?.status === 401 && config && !config._vaultRetried &&
      !String(config.url).startsWith("/vault/") && getToken()) {
    config._vaultRetried = true;
    await vaultResumeFounder();
    config.headers = { ...config.headers, "X-CSRF-Token": _csrf };
    return vaultApi(config);
  }
  return Promise.reject(error);
});

export async function vaultLogout() {
  try { await vaultApi.post("/vault/logout"); } finally { setVaultCsrf(null); }
}
export async function vaultListDocuments() {
  const { data } = await vaultApi.get("/admin/documents");
  return data;
}
export async function vaultUploadDocument(file, note = "", batchId = "", options = {}) {
  options.onStage?.("Checking for duplicates");
  if (window.crypto?.subtle) {
    const digest = await window.crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    const sha256 = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
    const {data: existing} = await vaultApi.post("/admin/documents/check-duplicate", {sha256});
    if (existing.skipped_duplicate) return existing;
  }
  options.onStage?.("Uploading");
  const fd = new FormData();
  fd.append("file", file);
  if (note) fd.append("note", note);
  if (batchId) fd.append("batch_id", batchId);
  if (options.previousFileId) fd.append("previous_file_id", options.previousFileId);
  const { data } = await vaultApi.post("/admin/documents/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
    onUploadProgress: options.onUploadProgress,
  });
  return data;
}
export async function vaultDownloadDocument(fileId) {
  const res = await vaultApi.get(`/admin/documents/${fileId}/download`, { responseType: "blob" });
  return res.data;
}
export async function vaultDeleteDocument(fileId) {
  const { data } = await vaultApi.delete(`/admin/documents/${fileId}`);
  return data;
}

export function formatApiError(detail, fallback = "Something went wrong. Please try again.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail && detail.msg) return String(detail.msg);
  if (detail && detail.message) return String(detail.message);
  if (detail && detail.error) return String(detail.error).replace(/_/g, " ");
  return fallback;
}


// ---------- Founder Close Console (Phase 3A.2 — read-only) ----------
export async function fetchCloseStatus() {
  const { data } = await api.get("/fin/close/status");
  return data;
}

export async function fetchCloseEntityDetail(entityId) {
  const { data } = await api.get(`/fin/close/entities/${entityId}`);
  return data;
}

export async function fetchClosePortfolio() {
  const { data } = await api.get("/fin/close/portfolio");
  return data;
}

export async function fetchCloseWhatChanged(entityId, priorSnapshotId, againstSnapshotId) {
  const params = {};
  if (priorSnapshotId) params.prior_snapshot_id = priorSnapshotId;
  if (againstSnapshotId) params.against_snapshot_id = againstSnapshotId;
  const { data } = await api.get(`/fin/close/entities/${entityId}/what-changed`, { params });
  return data;
}

export async function fetchClosePeriodView(entityId, periodId) {
  const params = {};
  if (periodId) params.period_id = periodId;
  const { data } = await api.get(`/fin/close/entities/${entityId}/period-view`, { params });
  return data;
}

export async function fetchPlanningView(entityId, fiscalYear) {
  const { data } = await api.get(`/fin/plan/entities/${entityId}/planning-view`, { params: { fiscal_year: fiscalYear } });
  return data;
}

export async function fetchAssetRegister(entityId) {
  const { data } = await api.get(`/fin/assets/entities/${entityId}/asset-register`);
  return data;
}

// ---------- Founder Tax Compliance (Phase 6 — read-only) ----------
export async function fetchTaxOverview() {
  const { data } = await api.get("/fin/tax-compliance/overview");
  return data;
}
export async function fetchTaxCalendar(entityId) {
  const params = {};
  if (entityId) params.entity_id = entityId;
  const { data } = await api.get("/fin/tax-compliance/calendar", { params });
  return data;
}

// ---------- Founder Time & Contribution Ledger (Phase 1 — Founder-only) ----------
export async function timeGetTimer() {
  const { data } = await api.get("/fin/time/timer");
  return data;
}
export async function timeStart(payload) {
  const { data } = await api.post("/fin/time/timer/start", payload);
  return data;
}
export async function timePause(command_id) {
  const { data } = await api.post("/fin/time/timer/pause", { command_id });
  return data;
}
export async function timeResume(command_id) {
  const { data } = await api.post("/fin/time/timer/resume", { command_id });
  return data;
}
export async function timeStop(payload) {
  const { data } = await api.post("/fin/time/timer/stop", payload);
  return data;
}
export async function timeDiscard(command_id) {
  const { data } = await api.post("/fin/time/timer/discard", { command_id });
  return data;
}
export async function timeHeartbeat(active_at) {
  const { data } = await api.post("/fin/time/timer/heartbeat", { active_at });
  return data;
}
export async function timeCreateEntry(payload) {
  const { data } = await api.post("/fin/time/entries", payload);
  return data;
}
export async function timeListEntries(params = {}) {
  const { data } = await api.get("/fin/time/entries", { params });
  return data;
}
export async function timeConfirmEntry(id, payload = {}) {
  const { data } = await api.post(`/fin/time/entries/${id}/confirm`, payload);
  return data;
}
export async function timeVoidEntry(id, reason) {
  const { data } = await api.post(`/fin/time/entries/${id}/void`, { reason });
  return data;
}
export async function timeLockEntry(id) {
  const { data } = await api.post(`/fin/time/entries/${id}/lock`, {});
  return data;
}
export async function timeCorrectEntry(id, payload) {
  const { data } = await api.post(`/fin/time/entries/${id}/correct`, payload);
  return data;
}
export async function timeGenerateSuggestions(lookback_hours = 168) {
  const { data } = await api.post(`/fin/time/suggestions/generate?lookback_hours=${lookback_hours}`, {});
  return data;
}
export async function timeListRuntime() {
  const { data } = await api.get("/fin/time/runtime");
  return data;
}
export async function timeReport(params = {}) {
  const { data } = await api.get("/fin/time/report", { params });
  return data;
}

export async function timeActivityTimeline(limit = 100) {
  const { data } = await api.get("/fin/time/activity/timeline", { params: { limit } });
  return data;
}

export async function timeWeeklyDigest(weekOf, tz) {
  const params = {};
  if (weekOf) params.week_of = weekOf;
  if (tz) params.tz = tz;
  const { data } = await api.get("/fin/time/weekly-digest", { params });
  return data;
}

// ---------- Governance Read-Models & Registers (P7/P8/P9 + Mesh Health) ----------
export async function govPayrollBoundaries(entityId) {
  const { data } = await api.get("/fin/governance/payroll-boundaries", { params: entityId ? { entity_id: entityId } : {} });
  return data;
}
export async function govListInsurance() { const { data } = await api.get("/fin/governance/insurance"); return data; }
export async function govCreateInsurance(payload) { const { data } = await api.post("/fin/governance/insurance", payload); return data; }
export async function govUpdateInsurance(id, payload) { const { data } = await api.patch(`/fin/governance/insurance/${id}`, payload); return data; }
export async function govListRisk() { const { data } = await api.get("/fin/governance/risk"); return data; }
export async function govCreateRisk(payload) { const { data } = await api.post("/fin/governance/risk", payload); return data; }
export async function govUpdateRisk(id, payload) { const { data } = await api.patch(`/fin/governance/risk/${id}`, payload); return data; }
export async function govListDR() { const { data } = await api.get("/fin/governance/dr"); return data; }
export async function govCreateDR(payload) { const { data } = await api.post("/fin/governance/dr", payload); return data; }
export async function govUpdateDR(id, payload) { const { data } = await api.patch(`/fin/governance/dr/${id}`, payload); return data; }
export async function govListStewardship() { const { data } = await api.get("/fin/governance/stewardship"); return data; }
export async function govCreateStewardship(payload) { const { data } = await api.post("/fin/governance/stewardship", payload); return data; }
export async function govUpdateStewardship(id, payload) { const { data } = await api.patch(`/fin/governance/stewardship/${id}`, payload); return data; }
export async function govAttention() { const { data } = await api.get("/fin/governance/attention"); return data; }
export async function govMeshHealth() { const { data } = await api.get("/fin/governance/mesh-health"); return data; }

// ---------- CFO Operating System ----------
export async function cfoSnapshot() { const { data } = await api.get("/fin/cfo/snapshot"); return data; }
export async function cfoReadiness() { const { data } = await api.get("/fin/cfo/readiness"); return data; }
export async function cfoGapReport() { const { data } = await api.get("/fin/cfo/gap-report"); return data; }
export async function cfoAnswers() { const { data } = await api.get("/fin/cfo/answers"); return data; }
export async function cfoBrief(period) { const { data } = await api.get(`/fin/cfo/brief/${period}`); return data; }
export async function cfoEntityPortfolio() { const { data } = await api.get("/fin/cfo/entity-portfolio"); return data; }
export async function cfoTimeSummary() { const { data } = await api.get("/fin/cfo/time/summary"); return data; }
export async function cfoHoursSheet(weeks = 8) { const { data } = await api.get(`/fin/cfo/time/hours-sheet?weeks=${weeks}`); return data; }
export async function cfoSetTimeRate(rate_per_hour, currency = "USD") { const { data } = await api.post("/fin/cfo/time/rate", { rate_per_hour, currency }); return data; }
export async function cfoForecast() { const { data } = await api.get("/fin/cfo/forecast"); return data; }
export async function cfoSourceHealth() { const { data } = await api.get("/fin/cfo/source-health"); return data; }
export async function cfoProviderReco() { const { data } = await api.get("/fin/cfo/provider-recommendation"); return data; }
